import { result, get, isEqual } from 'lodash';

import { fileDownload } from '../../common/services/fileUtils';
import APIClient from '../../common/services/APIClient';
import { listRequest, listRequestError, listRequestSuccess, setTotalCount } from '../../common/components/SSPList/actions';
import { DOMESTIC_CUSTOMER_INFO, loginError, logout } from '../Login/actions';
import { setMessage } from '../../common/components/Notification/actions';
import { transformQueryOut } from '../../common/components/AdvancedSearch/QueryBuilder/utils';
import { emptyQuery, OPERATOR_NAMES } from '../../common/components/AdvancedSearch/QueryBuilder/constants';
import { fillChargersFilter, getConfigurationPayload, TABS } from '../../common/components/AdvancedSearch/utils';
import { setMessageExportUnavailable } from '../../common/components/Alert/actions';

import { getChargersAdvancedSearchConfig } from './constants';
import { setCookie } from '../../common/cookies';
import { getActiveConditionWrapper } from '../Dashboard/actions';

export const togglePersonalizeModal = (show) => ({
  type: 'TOGGLE_PERSONALIZE_MODAL',
  show,
});

export const personalizeModalSubmit = (data) => ({
  type: 'PERSONALIZE_MODAL_SUBMIT',
  data,
});

export const setColumnsConfig = (config) => ({
  type: 'SET_COLUMNS_CONFIG',
  config,
});

export const getChargers =
  (id, { page_token: pageToken = 0, page_size: pageSize = 10, filterQuery, filter_by: filterBy, sorting = {}, wrapFilter }) =>
  (dispatch, getState) => {
    const {
      advancedSearch: { selectedTab },
    } = getState();

    const isConfigurationTabAvailable = selectedTab?.[id] === TABS.CONFIGURATION && !isEqual(filterQuery, emptyQuery);

    dispatch(listRequest(id));

    const queryOut = transformQueryOut([filterQuery || emptyQuery], getChargersAdvancedSearchConfig())[0];

    const filter = wrapFilter ? wrapFilter(queryOut) : queryOut;

    if (filterBy) {
      fillChargersFilter(filterBy, filter);
    }

    const payload = {
      filter_by: filter || {},
    };

    if (isConfigurationTabAvailable) {
      payload.config_nodes_filter = getConfigurationPayload(queryOut);
      payload.filter_by = wrapFilter ? getActiveConditionWrapper() : {};
    }

    APIClient.post('/chargers/info', payload, {
      params: {
        page_token: pageToken,
        page_size: pageSize,
        sort_by: sorting.id,
        sort_order: sorting.desc ? 'DESC' : 'ASC',
      },
    })
      .then((resp) => {
        const data = result(resp, 'data.payloads', null);
        const size = result(resp, 'data.total_size', 0);
        const filteredData = data.filter((item) => !!item);

        dispatch(listRequestSuccess(id, filteredData));
        dispatch(setTotalCount(id, size));
      })
      .catch((err) => {
        dispatch(listRequestError(id, err));
      });
  };

export const exportToCSV = (exportProps, wrapFilter) => (dispatch, getState) => {
  const { selectAll, items, sorting, filterQuery, filter, ssplistId } = exportProps;
  const {
    advancedSearch: { selectedTab },
  } = getState();

  const isConfigurationTab = selectedTab?.[ssplistId] === TABS.CONFIGURATION && !isEqual(filterQuery, emptyQuery);

  let filterBy = transformQueryOut([filterQuery || emptyQuery], getChargersAdvancedSearchConfig())[0];

  if (!selectAll) {
    filterBy = {
      operator: 'AND',
      conditions: [
        {
          field: 'c_uuids',
          operator: OPERATOR_NAMES.anyOf,
          value: items,
        },
      ],
    };
  } else if (filter) {
    fillChargersFilter(filter, filterBy);
  }

  if (wrapFilter && !isConfigurationTab) {
    filterBy = wrapFilter(filterBy);
  }

  const payload = {
    filter_by: filterBy || {},
  };

  if (isConfigurationTab && selectAll) {
    payload.config_nodes_filter = getConfigurationPayload(filterBy);
    payload.filter_by = {};
  }

  return APIClient.post('/chargers/info/export', payload, {
    params: {
      sort_by: sorting.id || 'c_uuid',
      sort_order: sorting.desc ? 'DESC' : 'ASC',
      exportType: 'USUAL',
    },
  })
    .then(({ data, headers }) => {
      fileDownload(data, 'exported-chargers.csv', headers['content-type']);
    })
    .catch((err) => {
      if (err.response && err.response.status === 500) {
        dispatch(setMessageExportUnavailable());
      }
    });
};

export const getDynamicFormValues = async () => {
  try {
    const {
      data: { configNodeNames },
    } = await APIClient.get('/dynamic-form-values/chargers-by-config-nodes-search-form');

    return configNodeNames.map((node) => ({
      name: node,
      label: node,
      value: node,
    }));
  } catch (err) {
    console.log('getDynamicFormValues error: ', err);
  }
};

export const loginToCustomerFromCharger =
  (props = {}) =>
  (dispatch, getState) => {
    const { customerURL, customerId, refreshToken, chargerURL } = props;

    APIClient.get('/users/login/customer', { params: { url: customerURL, customerId } })
      .then((resp) => {
        dispatch(
          logout(() => {
            window.location.href = `${customerURL}/login/customer?Authorization=${resp.headers.authorization}&refreshToken=${refreshToken}&redirect=${chargerURL}`;
          }),
        );

        const {
          login: {
            user: { customer_id: domestic_id, customer_url: domestic_url, domain },
          },
        } = getState();

        dispatch(
          setCookie(
            DOMESTIC_CUSTOMER_INFO,
            JSON.stringify({
              customer_id: domestic_id,
              customer_url: domestic_url,
            }),
            domain,
          ),
        );
      })
      .catch((response) => {
        const message = get(response, 'data.message', null);

        if (message) {
          dispatch(
            setMessage({
              message,
              duration: 3000,
            }),
          );
        } else {
          dispatch(setMessage({ message: 'Customer authorization failed', duration: 3000 }));
        }
        dispatch(loginError('Customer authorization failed'));
      });
  };
